import usePromos from "src/hooks/http/usePromos";
import { GetProductResponse } from "src/models/product";
import { dateType, currencyType } from "src/utils/dataGridFormat";
import { getArchiveTypeLabel } from "src/utils/archiveTypeMap";
import DataGrid from "./DataGrid";
import { GridColDef } from "@mui/x-data-grid-pro";
import {useCountry } from "src/components/Context/CountryContext";
import { getConsts } from "src/utils/consts";
import moment from "moment-timezone";

type PromotionTabProps = {
  product: GetProductResponse;
};

const PromotionTab: React.FC<PromotionTabProps> = ({ product }: PromotionTabProps) => {
  const { isLoading, data } = usePromos(product.sku);
  const { timezone } = useCountry();
  const consts = getConsts();


  const promoColumns: GridColDef[] = [
    { field: "id", headerName: "Id" },
    { field: "description", headerName: "Description", flex: 1 },
    { 
      field: "status", 
      headerName: "Status",
      renderCell: ({ value }) => (
        <span
          style={{
            backgroundColor: statusBackgroundColor(value),
            color: statusTextColor(value),
            padding: "4px 8px",
            borderRadius: "20px",
            borderColor: statusTextColor(value),
            borderStyle: "solid",
            borderWidth: "1px",
            fontWeight: 500,
            display: "inline-block",
            minWidth: 60,
            textAlign: "center",
          }}
        >
          {value}
        </span>
      ),
    },
    { field: "reason", headerName: "Reason", flex: 1 },
    { 
      field: "dateFrom", 
      headerName: "From", 
      valueFormatter: (params) => 
        params.value 
          ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY") 
          : ""
    },
    { 
      field: "dateTo", 
      headerName: "To", 
      valueFormatter: (params) => 
        params.value 
          ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY") 
          : ""
    },
    { field: "price", headerName: "Price", ...currencyType },
  ];

  const getPromoStatus = (dateFrom: any, dateTo: any) => {
    const now = moment();
    const from = moment(dateFrom);
    const to = moment(dateTo);

    if (now.isBefore(from)) {
      return 'Future';
    } else if (now.isBetween(from, to, null, '[]')) {
      return 'Active';
    } else {
      return 'Expired';
    }
  };

  // Sort promos by dateFrom in descending order and add status and reason
  const sortedData = data?.slice().map((promo) => {
    const status = getPromoStatus(promo.dateFrom, promo.dateTo);
    return {
      ...promo,
      status,
      reason: status === 'Expired' ? getArchiveTypeLabel(promo.archiveType) : '',
    };
  }).sort((a, b) => {
    if (moment(a.dateFrom) === null) return 1;
    if (moment(b.dateFrom) === null) return -1;
    return moment(b.dateFrom).diff(moment(a.dateFrom))
  });

  const statusBackgroundColor = (status: string) => {
    switch (status) {
      case 'Active':
        return '#E6F6EE';
      case 'Future':
        return '#E6F1F8';
      case 'Expired':
        return '#FDF2F2';
      default:
        return 'transparent';
    }
  };

  const statusTextColor = (status: string) => {
    switch (status) {
      case 'Active':
        return '#289A29';
      case 'Future':
        return '#4297CD';
      case 'Expired':
        return '#E74D4D';
      default:
        return 'black';
    }
  };


  return (
    <DataGrid
      isLoading={isLoading}
      dataGridId={consts.dataGridIds.promotionsDataGridId}
      defaultPageSize={100}
      marginTop={4}
      columns={promoColumns}
      rows={sortedData}
      hideFooter
    />
  );
};

export default PromotionTab;
